import {
  Block,
  DoorFrontOutlined,
  InfoOutlined,
  VisibilityOff,
} from "@mui/icons-material"
import { Box, Stack, Typography } from "@mui/material"
import { FC, ReactNode } from "react"
import { useTranslation } from "react-i18next"

/**
 * What the switch actually does, spelled out in the UI rather than left in a
 * commit message.
 *
 * This panel and the migration override sitting under it both end with "users
 * cannot see this locker", by two completely different mechanisms with different
 * durability and different blast radius. An operator reaching for either one is
 * usually mid-incident, and the failure mode of guessing wrong is real: hide the
 * wrong way and a partner event silently reverts you; repair the wrong way and
 * you rewrite migration state you did not mean to touch.
 *
 * So the consequences are enumerated — including the two things that keep
 * working, since "will the parcel already inside come out?" is the first question
 * an operator has, and an unanswered one gets answered by not using the feature.
 */

const Line: FC<{ icon: ReactNode; children: ReactNode }> = ({
  icon,
  children,
}) => (
  <Stack direction="row" spacing={1} alignItems="flex-start">
    <Box sx={{ color: "text.secondary", display: "flex", pt: "1px" }}>{icon}</Box>
    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.55 }}>
      {children}
    </Typography>
  </Stack>
)

const ICON_SX = { fontSize: 15 }

export const SaLockerServiceExplainer: FC = () => {
  const { t } = useTranslation()

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "action.hover",
      }}
    >
      <Stack direction="column" spacing={1}>
        <Typography variant="caption" fontWeight={600} color="text.primary">
          {t("lockerService.explainerTitle")}
        </Typography>

        <Line icon={<VisibilityOff sx={ICON_SX} />}>
          {t("lockerService.effectListing")}
        </Line>
        <Line icon={<Block sx={ICON_SX} />}>
          {t("lockerService.effectNewReservations")}
        </Line>
        <Line icon={<DoorFrontOutlined sx={ICON_SX} />}>
          {t("lockerService.effectExistingReservations")}
        </Line>
        <Line icon={<InfoOutlined sx={ICON_SX} />}>
          {t("lockerService.effectVsMigration")}
        </Line>
      </Stack>
    </Box>
  )
}

export default SaLockerServiceExplainer
